import { memo } from 'react'
import type * as Y from 'yjs'
import type { BoardElement, BoardTheme, FontDto, MediaDto } from '@praesto/shared'
import type { CameraStore } from './CameraStore.js'
import { IframeBody } from './IframeBody.js'
import { TextBody } from './TextBody.js'
import { useAdaptiveImage, useIsVisible, useRequiredLevel } from './useAdaptiveImage.js'

/**
 * Ecken *und* Kanten.
 *
 * Ecken ändern beide Maße, Kanten nur eines. Für Bilder und Videos reichen die
 * Ecken — ihr Seitenverhältnis ist fest, eine Kante hätte dort keine eigene
 * Bedeutung. Textfelder bekommen die Kanten: Breite und Höhe sind dort
 * unabhängig, und genau das braucht man beim Setzen einer Folie.
 */
export type ResizeCorner = 'nw' | 'ne' | 'se' | 'sw' | 'n' | 'e' | 's' | 'w'

export interface ElementViewProps {
  element: BoardElement
  media: MediaDto | undefined
  camera: CameraStore
  selected: boolean
  /**
   * Einziges ausgewähltes Objekt.
   *
   * Alles, was sich nur auf eines anwenden lässt, hängt hieran: Anfasser zum
   * Skalieren, Texteingabe, Bedienung einer eingebetteten Seite.
   */
  sole: boolean
  canEdit: boolean
  onDragStart: (event: React.PointerEvent, element: BoardElement) => void
  onResizeStart: (event: React.PointerEvent, element: BoardElement, corner: ResizeCorner) => void
  /** Darf die eingebettete Seite bedient werden? Im streng geführten Modus nicht. */
  allowIframeInteraction: boolean
  /** Schreibrecht am Dokument — im Vortrag weiterhin gegeben. */
  canWrite: boolean
  theme: BoardTheme
  /** Selbst gehostete Schriften der Organisation; nur die Textleiste braucht sie. */
  fonts: readonly FontDto[]
  /** Nimmt ein Textfeld beim Ziehen an einer Ecke die Schriftgröße mit? */
  scaleFont: boolean
  onToggleScaleFont?: () => void
  onEditIframeUrl: (id: string) => void
  /** Holt das Yjs-Fragment eines Textelements — erst beim Bearbeiten. */
  onEditText: (id: string) => Promise<Y.XmlFragment | null>
  onPatchElement: (id: string, patch: Partial<BoardElement>) => void
}

const CORNERS: ResizeCorner[] = ['nw', 'ne', 'se', 'sw']
const EDGES: ResizeCorner[] = ['n', 'e', 's', 'w']

function ImageBody({
  element,
  media,
  camera,
  visible,
}: {
  element: BoardElement
  media: MediaDto | undefined
  camera: CameraStore
  visible: boolean
}): React.ReactElement {
  const level = useRequiredLevel(camera, element.w, media)
  const loaded = useAdaptiveImage(media, level, visible)

  if (!loaded) return <div className="element-placeholder" aria-hidden="true" />
  return (
    <img
      className="media-image"
      src={loaded.url}
      alt={media?.originalName ?? ''}
      draggable={false}
    />
  )
}

function VideoBody({
  media,
  selected,
}: {
  media: MediaDto | undefined
  selected: boolean
}): React.ReactElement {
  const source = media?.variants.find((variant) => variant.level === 'orig')
  if (!source) return <div className="element-placeholder" aria-hidden="true" />
  return (
    <video
      className="media-video"
      src={source.url}
      // Steuerung nur bei Auswahl: sonst fängt die Videoleiste jeden Klick ab,
      // mit dem man das Element eigentlich verschieben wollte.
      controls={selected}
      preload="metadata"
      playsInline
    />
  )
}

export const ElementView = memo(function ElementView({
  element,
  media,
  camera,
  selected,
  sole,
  canEdit,
  onDragStart,
  onResizeStart,
  allowIframeInteraction,
  canWrite,
  theme,
  fonts,
  scaleFont,
  onToggleScaleFont,
  onEditIframeUrl,
  onEditText,
  onPatchElement,
}: ElementViewProps): React.ReactElement | null {
  const visible = useIsVisible(camera, element)

  // Vollständig ausgeblendete Elemente werden gar nicht erst gerendert; das
  // hält die Anzahl der Compositor-Layer bei großen Boards im Rahmen.
  if (!visible && !selected) return null

  return (
    <div
      className={`element element-${element.type}${selected ? ' is-selected' : ''}`}
      style={{
        left: `${element.x}px`,
        top: `${element.y}px`,
        width: `${element.w}px`,
        height: `${element.h}px`,
      }}
      onPointerDown={(event) => {
        if (!canEdit) return
        onDragStart(event, element)
      }}
      data-element-id={element.id}
    >
      {element.type === 'image' && (
        <ImageBody element={element} media={media} camera={camera} visible={visible} />
      )}
      {element.type === 'video' && (
        <VideoBody media={media} selected={sole || !canEdit} />
      )}
      {element.type === 'text' && (
        <TextBody
          element={element}
          selected={sole}
          editable={canEdit}
          theme={theme}
          fonts={fonts}
          camera={camera}
          onChange={(patch) => onPatchElement(element.id, patch)}
          onMeasured={(height) => onPatchElement(element.id, { h: height })}
          onEditFragment={() => onEditText(element.id)}
          scaleFont={scaleFont}
          onToggleScaleFont={onToggleScaleFont}
        />
      )}
      {element.type === 'iframe' && (
        <IframeBody
          element={element}
          selected={sole}
          canEdit={canEdit}
          canWrite={canWrite}
          allowInteraction={allowIframeInteraction}
          camera={camera}
          onChange={(patch) => onPatchElement(element.id, patch)}
          onEditUrl={() => onEditIframeUrl(element.id)}
        />
      )}

      {selected && canEdit && <div className="element-outline" aria-hidden="true" />}

      {/* Anfasser nur bei genau einem ausgewählten Objekt: Mehrere zugleich zu
          skalieren wäre eine eigene Rechnung — und ein Anfasser, der etwas
          anderes tut als er aussieht, ist schlimmer als keiner. */}
      {sole && canEdit && (
        <>
          {[...CORNERS, ...(element.type === 'text' ? EDGES : [])].map((corner) => (
            <div
              key={corner}
              className={`handle handle-${corner}`}
              onPointerDown={(event) => {
                event.stopPropagation()
                onResizeStart(event, element, corner)
              }}
            />
          ))}
        </>
      )}
    </div>
  )
})
